// src/ui/components/DocumentPreflightModal.js
// Pre-flight validation modal shown before generating any official PDF document.
// - Validates company data (CIF + IBAN required)
// - Validates contact NIF (required for Contrato and Factura only)
// - Allows inline editing of contact NIF if missing
// - Shows the document number preview
// - Only enables "Generate" button when all checks pass

import { getTeamSettings, buildCompanyData, validateDocumentData, previewNextDocNumber, incrementDocNumber } from "../../services/documentService.js";
import { updateContact, createContact, listContactsForEvent } from "../../services/contactsService.js";
import { getPrimaryVenueForEvent, formatVenueForDocument, addEventContact } from "../../services/eventsService.js";
import { CONTACT_TYPES } from "../../domain/constants.js";
import { getCurrentTeamId } from "../../services/teamService.js";
import { actions } from "../../app/state.js";
import { onSafeBackdropClick } from "../modalUtils.js";
import { mountRoute } from "../../app/router.js";
import { splitHonorarios, normalizeReservaPct, DEFAULT_RESERVA_PCT } from "../../services/pdf/generateContrato.js";
import { updateEvent } from "../../services/eventsService.js";

// Los valores del usuario se inyectan en atributos value="..." de una plantilla
// HTML: sin escapar, una comilla en un apellido rompe el marcado del modal.
function escapeAttr(str) {
    return String(str ?? "")
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// Mismo criterio que el PDF: separador de millares siempre, para que el
// desglose del modal y el del contrato no difieran en la forma de escribir.
function fmt(n) {
    return new Intl.NumberFormat("es-ES", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        useGrouping: "always",
    }).format(Number(n) || 0) + " €";
}


/**
 * Opens the document pre-flight modal.
 */
export async function openDocumentPreflightModal({
    event,
    contact,
    docType,
    docTypeCode,
    docLabel,
    onGenerate,
}) {
    // 1. Load data
    const teamId = await getCurrentTeamId({ profileOptions: { force: false, allowNull: true } });
    const settings = await getTeamSettings(teamId);
    const company = buildCompanyData(settings);

    // merge contact with fresh data
    let enrichedContact = { ...contact };

    // El contrato sustituye la tarjeta de facturacion por la de firmantes:
    // mismos datos (nombre, DNI, domicilio) pero referidos a quien firma, que
    // no tiene por que ser el contacto principal del evento.
    const isContrato = docType === "contrato";

    // Los firmantes salen de los contactos del evento, no solo del primary:
    // en una boda lo normal es tener ficha de los dos novios.
    let eventContacts = [];
    let venueLabel = "";
    if (isContrato && event?.id) {
        [eventContacts, venueLabel] = await Promise.all([
            listContactsForEvent(event.id).catch((e) => {
                console.warn("[Preflight] no se pudieron cargar los contactos del evento:", e);
                return [];
            }),
            getPrimaryVenueForEvent(event.id)
                .then(formatVenueForDocument)
                .catch((e) => {
                    console.warn("[Preflight] no se pudo cargar el venue del evento:", e);
                    return "";
                }),
        ]);

        // El primary siempre encabeza la lista: es el firmante 1 por defecto.
        if (contact?.id && !eventContacts.some((c) => c.id === contact.id)) {
            eventContacts = [contact, ...eventContacts];
        }
    }

    const contactLabel = (c) => [c.name, c.surname].filter(Boolean).join(" ").trim() || c.email || "Sin nombre";
    const contactDomicilio = (c) => [c.fiscal_address, c.fiscal_zip, c.fiscal_city].filter(Boolean).join(", ");

    // El desplegable se repinta cuando el guardado crea una ficha nueva.
    const signerOptions = (n) => `
        <option value="">Escribir a mano</option>
        ${eventContacts.map(c => `
          <option value="${escapeAttr(c.id)}"${c.id === contractState[`signer${n}`].contactId ? " selected" : ""}>
            ${escapeAttr(contactLabel(c))}${c.is_primary ? " (principal)" : ""}
          </option>`).join("")}`;

    // "Ane Etxebarria Goikoetxea" -> name "Ane", surname "Etxebarria Goikoetxea",
    // que es como ContactsModal parte el nombre y como contactLabel lo recompone.
    const splitName = (full) => {
        const parts = String(full || "").trim().split(/\s+/);
        return { name: parts.shift() || "", surname: parts.join(" ") || null };
    };

    // 2. Initial Validate
    const errors = validateDocumentData({ company, contact: enrichedContact, docType });

    // 3. Status flags
    const hasCifError   = errors.some(e => e.field === "company_cif");
    const hasIbanError  = errors.some(e => e.field === "company_iban");
    const hasNifError   = errors.some(e => e.field === "contact_nif");

    // Estado del contrato. Arranca con lo que sepamos del contacto principal
    // y el resto lo completa el usuario en el modal.
    // Cada hueco se propone con un contacto del evento (1º y 2º) y queda
    // atado a su id, para poder devolverle despues el DNI corregido.
    const signerFrom = (c, treatment) => ({
        contactId: c?.id || "",
        treatment,
        name: c ? contactLabel(c) : "",
        nif: c?.nif || "",
    });

    const c1 = eventContacts[0] || (enrichedContact.id ? enrichedContact : null);
    const c2 = eventContacts.find((c) => c.id !== c1?.id) || null;

    const contractState = {
        signer1: signerFrom(c1, "Don"),
        signer2: signerFrom(c2, "Doña"),
        // El domicilio de notificaciones es uno solo para ambos: se propone el
        // del primer firmante que lo tenga relleno.
        domicilio: contactDomicilio(c1 || {}) || contactDomicilio(c2 || {}) || contactDomicilio(enrichedContact),
        contacto: [c1?.email || c1?.phone, c2?.email || c2?.phone].filter(Boolean).join(" / ")
            || enrichedContact.email || enrichedContact.phone || "",
        lugarEvento: venueLabel || event?.location || event?.city || event?.region || "",
        lugarFirma: company.city || "",
        honorariosBase: Number(event?.amount || 0),
        // El reparto pactado vive en el evento: asi el contrato y los avisos de
        // confirmacion hablan del mismo porcentaje.
        reservaPct: normalizeReservaPct(event?.deposit_pct ?? DEFAULT_RESERVA_PCT),
    };

    const contractErrors = () => {
        const e = [];
        if (!contractState.signer1.name.trim()) e.push("Nombre del firmante 1");
        if (!contractState.signer1.nif.trim())  e.push("DNI del firmante 1");
        if (!contractState.signer2.name.trim()) e.push("Nombre del firmante 2");
        if (!contractState.signer2.nif.trim())  e.push("DNI del firmante 2");
        if (!contractState.lugarEvento.trim())  e.push("Lugar del evento");
        if (!(contractState.honorariosBase > 0)) e.push("Honorarios del evento");
        return e;
    };

    // Para el contrato, el boton Generar solo atiende a los datos de empresa:
    // los firmantes se validan al pulsar, porque se teclean en el propio modal.
    const initialErrors = isContrato
        ? errors.filter(e => e.scope === "company")
        : errors;

    const modalEl = document.createElement("div");
    modalEl.id = "doc-preflight-modal-instance";
    modalEl.className = "fixed inset-0 z-[300] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4";

    // Build Internal CSS for the checkbox to ensure visibility and size
    const styleTag = document.createElement("style");
    styleTag.textContent = `
        .preflight-custom-check {
            width: 1.25rem !important;
            height: 1.25rem !important;
            cursor: pointer !important;
            accent-color: #2563eb;
        }
        .preflight-clickable-label {
            cursor: pointer !important;
            user-select: none !important;
        }
    `;
    modalEl.appendChild(styleTag);

    modalEl.innerHTML += `
      <div class="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
        <!-- Header -->
        <div class="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <h2 class="text-lg font-extrabold text-slate-900 flex items-center gap-2">
              <i class="fa-solid fa-magnifying-glass text-slate-400 text-base"></i>
              Verificación previa: ${docLabel}
            </h2>
            <p class="text-xs text-slate-500 mt-0.5">Confirma los datos antes de generar el documento oficial</p>
          </div>
          <button class="btn-close-modal text-slate-400 hover:text-slate-700 p-1 rounded-lg transition-colors">
            <i class="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <div class="p-6 space-y-4 max-h-[70vh] overflow-y-auto">

          <!-- COMPANY SECTION -->
          <div class="rounded-xl border ${(hasCifError || hasIbanError) ? "border-amber-200 bg-amber-50" : "border-emerald-200 bg-emerald-50/60"} p-4">
            <div class="flex items-center gap-2 mb-2">
              <i class="fa-solid fa-building ${(hasCifError || hasIbanError) ? "text-amber-500" : "text-emerald-500"} text-sm"></i>
              <span class="text-xs font-bold uppercase tracking-wider ${(hasCifError || hasIbanError) ? "text-amber-700" : "text-emerald-700"}">
                Empresa — ${ (hasCifError || hasIbanError) ? "Datos incompletos" : "OK" }
              </span>
            </div>
            <div class="text-sm text-slate-700 space-y-1">
              <div class="flex items-center gap-2">
                <i class="fa-solid ${!hasCifError ? "fa-check text-emerald-500" : "fa-triangle-exclamation text-amber-500"} text-xs w-3"></i>
                <span><strong>CIF:</strong> ${company.cif || "—"}</span>
              </div>
              <div class="flex items-center gap-2">
                <i class="fa-solid ${!hasIbanError ? "fa-check text-emerald-500" : "fa-triangle-exclamation text-amber-500"} text-xs w-3"></i>
                <span><strong>IBAN:</strong> ${company.iban || "—"}</span>
              </div>
            </div>
            ${(hasCifError || hasIbanError) ? `
              <button class="btn-go-settings mt-3 px-3 py-1.5 rounded-lg bg-amber-100 text-amber-800 text-xs font-bold hover:bg-amber-200 transition-colors flex items-center gap-1.5">
                <i class="fa-solid fa-arrow-right text-[10px]"></i>
                Completar en Ajustes
              </button>
            ` : ""}
          </div>

          ${isContrato ? `
          <!-- SIGNERS SECTION (solo contrato) -->
          <div class="signers-card rounded-xl border border-slate-200 bg-white p-4 space-y-4">
            <div class="flex items-center gap-2">
              <i class="fa-solid fa-file-signature text-slate-400 text-sm"></i>
              <span class="text-xs font-bold uppercase tracking-wider text-slate-700">Firmantes del contrato</span>
            </div>

            ${[1, 2].map(n => `
              <div class="space-y-2">
                <div class="flex items-center justify-between gap-2">
                  <p class="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Firmante ${n}</p>
                  <select class="sel-s${n}-contact max-w-[60%] px-2 py-1 rounded-lg border border-slate-200 text-[11px] bg-slate-50 text-slate-600 focus:border-slate-400 outline-none">
                    ${signerOptions(n)}
                  </select>
                </div>
                <div class="flex gap-2">
                  <select class="sel-s${n}-treatment w-24 px-2 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-slate-400 outline-none">
                    <option value="Don"${n === 1 ? " selected" : ""}>Don</option>
                    <option value="Doña"${n === 2 ? " selected" : ""}>Doña</option>
                    <option value="D./Dña.">D./Dña.</option>
                  </select>
                  <input type="text" class="inp-s${n}-name flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400"
                    placeholder="Nombre y apellidos" value="${escapeAttr(contractState[`signer${n}`].name)}">
                </div>
                <input type="text" class="inp-s${n}-nif w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:border-slate-400"
                  placeholder="DNI" value="${escapeAttr(contractState[`signer${n}`].nif)}">
              </div>
            `).join("")}

            <div class="pt-1 space-y-2 border-t border-slate-100">
              <div class="pt-2">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Domicilio a efectos de notificaciones</label>
                <input type="text" class="inp-domicilio w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400"
                  placeholder="Calle, CP, ciudad" value="${escapeAttr(contractState.domicilio)}">
              </div>
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Canal de contacto</label>
                <input type="text" class="inp-contacto w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400"
                  placeholder="Email o teléfono" value="${escapeAttr(contractState.contacto)}">
              </div>
            </div>

            <div class="flex items-center justify-between gap-3 pt-1">
              <p class="text-[10px] text-slate-400 leading-tight">
                Guardar actualiza la ficha de cada firmante elegido y crea la de quien no la tenga,
                vinculándola al evento. El contrato se genera igual sin guardar.
              </p>
              <button class="btn-save-signers shrink-0 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors flex items-center gap-1.5 disabled:opacity-40">
                <i class="fa-solid fa-floppy-disk text-[10px]"></i>
                Guardar en contacto
              </button>
            </div>
          </div>

          <!-- CONTRACT ECONOMICS -->
          <div class="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
            <div class="flex items-center gap-2">
              <i class="fa-solid fa-euro-sign text-slate-400 text-sm"></i>
              <span class="text-xs font-bold uppercase tracking-wider text-slate-700">Datos del contrato</span>
            </div>

            <div>
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Lugar del evento
                ${venueLabel ? `<span class="text-emerald-600 normal-case tracking-normal font-semibold">· del venue</span>` : ""}
              </label>
              <input type="text" class="inp-lugar-evento w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400"
                placeholder="Finca Las Margas, Segovia" value="${escapeAttr(contractState.lugarEvento)}">
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Lugar de firma</label>
                <input type="text" class="inp-lugar-firma w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400"
                  placeholder="Bilbao" value="${escapeAttr(contractState.lugarFirma)}">
              </div>
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Honorarios (base, sin IVA)</label>
                <input type="number" step="0.01" min="0" class="inp-honorarios w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:border-slate-400"
                  value="${contractState.honorariosBase || ""}">
              </div>
            </div>

            <div>
              <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Reparto del pago</label>
              <div class="flex items-center gap-2">
                <div class="relative w-24">
                  <input type="number" step="1" min="1" max="99" class="inp-reserva-pct w-full px-3 py-2 pr-7 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:border-slate-400"
                    value="${contractState.reservaPct}">
                  <span class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
                </div>
                <span class="text-[11px] text-slate-500">a la firma, en concepto de reserva</span>
              </div>
              <p class="txt-resto-pct text-[10px] text-slate-400 mt-1.5"></p>
            </div>

            <div class="breakdown rounded-lg bg-slate-50 border border-slate-100 p-3 text-xs font-mono space-y-1"></div>
          </div>
          ` : `
          <!-- CONTACT SECTION -->
          <div class="contact-card rounded-xl border ${hasNifError ? "border-rose-200 bg-rose-50/60" : "border-emerald-200 bg-emerald-50/60"} p-4 transition-all duration-300">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <i class="fa-solid fa-user-tie ${hasNifError ? "text-rose-500" : "text-emerald-500"} text-sm"></i>
                <span class="text-xs font-bold uppercase tracking-wider ${hasNifError ? "text-rose-700" : "text-emerald-700"}">
                  Datos de Facturación
                </span>
              </div>
              <button class="btn-edit-contact text-[10px] font-bold text-blue-600 hover:text-blue-800 uppercase tracking-widest flex items-center gap-1 transition-colors">
                <i class="fa-solid fa-pen-to-square"></i>
                Cambiar
              </button>
            </div>

            <!-- Summary View -->
            <div class="contact-summary text-sm text-slate-700 space-y-1">
              <div class="flex items-center gap-2">
                <i class="fa-solid fa-check text-emerald-500 text-xs w-3"></i>
                <span><strong>Nombre:</strong> <span class="txt-name">${(enrichedContact.name || "") + (enrichedContact.surname ? " " + enrichedContact.surname : "") || "—"}</span></span>
              </div>
              <div class="flex items-center gap-2">
                <i class="fa-solid ${!hasNifError ? "fa-check text-emerald-500" : "fa-circle-xmark text-rose-500"} text-xs w-3"></i>
                <span><strong>NIF/CIF:</strong> <span class="txt-nif font-mono">${enrichedContact.nif || "—"}</span></span>
              </div>
              <div class="flex items-center gap-2">
                <i class="fa-solid fa-check text-emerald-500 text-xs w-3 opacity-40"></i>
                <span class="text-xs text-slate-500 truncate overflow-hidden italic">
                  <span class="txt-addr">${enrichedContact.fiscal_address || "Sin dirección"}</span>, 
                  <span class="txt-city">${enrichedContact.fiscal_city || "—"}</span>
                </span>
              </div>
            </div>

            <!-- Edit Form (Hidden) -->
            <div class="contact-edit-form hidden mt-4 pt-4 border-t border-slate-200/60 space-y-3">
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Nombre / Razón Social *</label>
                <input type="text" class="inp-name w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400" value="${(enrichedContact.name || "") + (enrichedContact.surname ? " " + enrichedContact.surname : "")}">
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div>
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">NIF / CIF *</label>
                  <input type="text" class="inp-nif w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono focus:border-slate-400" value="${enrichedContact.nif || ""}">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Email</label>
                  <input type="email" class="inp-email w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-slate-400" value="${enrichedContact.email || ""}">
                </div>
              </div>
              <div>
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dirección Fiscal</label>
                <input type="text" class="inp-addr w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-slate-400" value="${enrichedContact.fiscal_address || ""}">
              </div>
              <div class="grid grid-cols-2 gap-3 pb-1">
                <div>
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Ciudad</label>
                  <input type="text" class="inp-city w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-slate-400" value="${enrichedContact.fiscal_city || ""}">
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Código Postal</label>
                  <input type="text" class="inp-zip w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-slate-400" value="${enrichedContact.fiscal_zip || ""}">
                </div>
              </div>

              <!-- THE CHECKBOX SECTION -->
              <div class="flex items-center gap-3 py-2 bg-slate-50 rounded-lg px-3 border border-slate-100">
                <input type="checkbox" id="chk-save-permanent" class="preflight-custom-check h-5 w-5 rounded border-slate-300 text-blue-600" checked>
                <label for="chk-save-permanent" class="preflight-clickable-label text-[11px] text-slate-700 font-medium">
                  Guardar cambios permanentemente en el contacto
                </label>
              </div>

              <div class="flex gap-2 pt-2">
                <button class="btn-cancel-edit flex-1 px-3 py-2 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">Cancelar</button>
                <button class="btn-apply-edit flex-1 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors">Aplicar</button>
              </div>
            </div>
          </div>
          `}

          <!-- DOC NUMBER PREVIEW -->
          <div class="docnum-card rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div class="flex items-center justify-between">
              <div>
                <div class="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Próximo nº disponible</div>
                <div class="txt-docnum text-base font-mono font-bold text-slate-900">
                  <i class="fa-solid fa-circle-notch fa-spin text-slate-400 text-xs"></i> Cargando...
                </div>
              </div>
              <div class="text-[10px] text-slate-400 text-right">
                <div class="font-bold uppercase tracking-widest">Estimado</div>
                <div class="text-[9px]">Se consume al generar</div>
              </div>
            </div>
          </div>

          <!-- ERROR BANNER -->
          <div class="error-banner hidden rounded-xl border border-rose-200 bg-rose-50 p-3">
            <p class="text-xs text-rose-700 font-medium flex items-center gap-2">
              <i class="fa-solid fa-triangle-exclamation text-rose-500"></i>
              Faltan datos obligatorios para generar este documento.
            </p>
          </div>

        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end items-center">
          <button class="btn-cancel-modal px-4 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors">
            Cancelar
          </button>
          <button class="btn-generate-doc px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20 flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
             ${initialErrors.length > 0 ? "disabled" : ""}>
            <i class="fa-solid fa-file-pdf text-xs"></i>
            Generar ${docLabel}
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(modalEl);

    const close = () => modalEl.remove();

    // -- WIRE EVENTS (using modalEl as root to avoid global lookup issues) --
    
    // Close
    modalEl.querySelector(".btn-close-modal").onclick = close;
    modalEl.querySelector(".btn-cancel-modal").onclick = close;
    onSafeBackdropClick(modalEl, close);

    // Settings
    modalEl.querySelector(".btn-go-settings")?.addEventListener("click", () => {
        close();
        window.CRM?.router?.mountRoute?.("settings");
    });

    // Edit logic (la tarjeta de facturacion no existe en el flujo de contrato)
    const editBtn = modalEl.querySelector(".btn-edit-contact");
    const summary = modalEl.querySelector(".contact-summary");
    const editForm = modalEl.querySelector(".contact-edit-form");

    const toggleEdit = (show) => {
        if (!editBtn || !summary || !editForm) return;
        if (show) {
            summary.classList.add("hidden");
            editForm.classList.remove("hidden");
            editBtn.classList.add("hidden");
        } else {
            summary.classList.remove("hidden");
            editForm.classList.add("hidden");
            editBtn.classList.remove("hidden");
        }
    };

    if (editBtn) editBtn.onclick = () => toggleEdit(true);
    const cancelEditBtn = modalEl.querySelector(".btn-cancel-edit");
    if (cancelEditBtn) cancelEditBtn.onclick = () => toggleEdit(false);

    // Apply logic
    const applyEditBtn = modalEl.querySelector(".btn-apply-edit");
    if (applyEditBtn) applyEditBtn.onclick = async () => {
        const nameVal = modalEl.querySelector(".inp-name").value.trim();
        const nifVal = modalEl.querySelector(".inp-nif").value.trim();
        const emailVal = modalEl.querySelector(".inp-email").value.trim();
        const addrVal = modalEl.querySelector(".inp-addr").value.trim();
        const cityVal = modalEl.querySelector(".inp-city").value.trim();
        const zipVal = modalEl.querySelector(".inp-zip").value.trim();
        const shouldSave = modalEl.querySelector("#chk-save-permanent").checked;

        if (!nameVal || !nifVal) {
            actions.addToast?.({ type: "warning", message: "Nombre y NIF son obligatorios" });
            return;
        }

        // Update local object
        enrichedContact = {
            ...enrichedContact,
            name: nameVal,
            surname: "", 
            nif: nifVal,
            email: emailVal,
            fiscal_address: addrVal,
            fiscal_city: cityVal,
            fiscal_zip: zipVal
        };

        if (shouldSave) {
            try {
                const btn = modalEl.querySelector(".btn-apply-edit");
                const oldHtml = btn.innerHTML;
                btn.disabled = true;
                btn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin"></i>';
                
                await updateContact(contact.id, {
                    name: nameVal,
                    surname: "",
                    nif: nifVal,
                    email: emailVal,
                    fiscal_address: addrVal,
                    fiscal_city: cityVal,
                    fiscal_zip: zipVal
                });
                actions.addToast?.({ type: "success", message: "Datos guardados en el contacto" });
                btn.disabled = false;
                btn.innerHTML = oldHtml;
            } catch (err) {
                console.error("Error updating contact:", err);
                actions.addToast?.({ type: "error", message: "Error al guardar permanentemente" });
            }
        }

        // Update UI
        modalEl.querySelector(".txt-name").textContent = nameVal;
        modalEl.querySelector(".txt-nif").textContent = nifVal;
        modalEl.querySelector(".txt-addr").textContent = addrVal || "Sin dirección";
        modalEl.querySelector(".txt-city").textContent = cityVal || "—";

        // Re-validate
        const newErrors = validateDocumentData({ company, contact: enrichedContact, docType });
        const genBtn = modalEl.querySelector(".btn-generate-doc");
        const banner = modalEl.querySelector(".error-banner");
        const card = modalEl.querySelector(".contact-card");

        if (genBtn) genBtn.disabled = newErrors.length > 0;
        if (newErrors.length === 0) {
            banner?.classList.add("hidden");
            card.classList.remove("border-rose-200", "bg-rose-50/60");
            card.classList.add("border-emerald-200", "bg-emerald-50/60");
        }

        toggleEdit(false);
    };

    // ── Contract block: desglose en vivo, guardado explicito y validacion ──
    if (isContrato) {
        const $ = (sel) => modalEl.querySelector(sel);

        const syncContractState = () => {
            for (const n of [1, 2]) {
                contractState[`signer${n}`] = {
                    contactId: $(`.sel-s${n}-contact`)?.value || "",
                    treatment: $(`.sel-s${n}-treatment`).value,
                    name: $(`.inp-s${n}-name`).value.trim(),
                    nif: $(`.inp-s${n}-nif`).value.trim().toUpperCase(),
                };
            }
            contractState.domicilio = $(".inp-domicilio").value.trim();
            contractState.contacto = $(".inp-contacto").value.trim();
            contractState.lugarEvento = $(".inp-lugar-evento").value.trim();
            contractState.lugarFirma = $(".inp-lugar-firma").value.trim();
            contractState.honorariosBase = Number($(".inp-honorarios").value) || 0;
            // Sin normalizar, un 0 o un 150 recien tecleados romperian el
            // desglose que se repinta en cada pulsacion.
            contractState.reservaPct = normalizeReservaPct($(".inp-reserva-pct").value);
        };

        // Elegir un contacto rellena su hueco; "Escribir a mano" deja lo tecleado.
        for (const n of [1, 2]) {
            const sel = $(`.sel-s${n}-contact`);
            if (!sel) continue;
            sel.addEventListener("change", () => {
                const picked = eventContacts.find(c => c.id === sel.value);
                if (!picked) { syncContractState(); return; }

                $(`.inp-s${n}-name`).value = contactLabel(picked);
                $(`.inp-s${n}-nif`).value = picked.nif || "";

                // El domicilio es comun a los dos firmantes: solo se pisa si
                // esta vacio o si lo elige el firmante 1.
                const dom = contactDomicilio(picked);
                const domInput = $(".inp-domicilio");
                if (dom && (n === 1 || !domInput.value.trim())) domInput.value = dom;

                syncContractState();
            });
        }

        const renderBreakdown = () => {
            const m = splitHonorarios(contractState.honorariosBase, contractState.reservaPct);
            $(".txt-resto-pct").textContent =
                `El ${m.restoPct} % restante se abona el lunes previo al evento.`;
            const row = (label, base, total, strong = false) => `
                <div class="flex justify-between ${strong ? "font-bold text-slate-900 pt-1 border-t border-slate-200" : "text-slate-600"}">
                  <span>${label}</span>
                  <span>${fmt(base)} + IVA = ${fmt(total)}</span>
                </div>`;
            $(".breakdown").innerHTML =
                row(`Reserva ${m.reservaPct} %`, m.reservaBase, m.reservaTotal)
                + row(`Resto ${m.restoPct} %`, m.restoBase, m.restoTotal)
                + row("Total", m.base, m.total, true);
        };

        modalEl.querySelectorAll(
            ".signers-card input, .signers-card select, .inp-lugar-evento, .inp-lugar-firma, "
            + ".inp-honorarios, .inp-reserva-pct"
        ).forEach(el => {
            el.addEventListener("input", () => { syncContractState(); renderBreakdown(); });
            el.addEventListener("change", () => { syncContractState(); renderBreakdown(); });
        });

        renderBreakdown();

        // Guardado explicito: boton con estado propio, nunca acoplado a Generar.
        const saveBtn = $(".btn-save-signers");
        saveBtn.onclick = async () => {
            syncContractState();

            // Un firmante con contacto elegido actualiza su ficha; uno tecleado
            // a mano estrena ficha y queda vinculado al evento, que es la unica
            // forma de que su DNI sobreviva al cierre del modal.
            const targets = [1, 2]
                .map((n) => contractState[`signer${n}`])
                .filter((s) => s.name && s.nif);

            if (!targets.length) {
                actions.addToast?.({
                    type: "warning",
                    message: "Rellena al menos el nombre y el DNI de un firmante para guardar",
                });
                return;
            }

            const original = saveBtn.innerHTML;
            let creados = 0;
            try {
                saveBtn.disabled = true;
                saveBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin text-[10px]"></i> Guardando...';

                for (const signer of targets) {
                    // El domicilio solo se escribe si hay algo: mandarlo vacio
                    // borraria el que ya tuviera la ficha.
                    const fiscal = { nif: signer.nif };
                    if (contractState.domicilio) fiscal.fiscal_address = contractState.domicilio;

                    if (signer.contactId) {
                        await updateContact(signer.contactId, fiscal);
                        const cached = eventContacts.find((c) => c.id === signer.contactId);
                        // La lista en memoria tambien, para que reabrir el
                        // desplegable no reponga el DNI viejo.
                        if (cached) Object.assign(cached, fiscal);
                        continue;
                    }

                    if (!teamId || !event?.id) {
                        throw new Error("Falta el equipo o el evento para crear el contacto");
                    }

                    const nuevo = await createContact({
                        team_id: teamId,
                        type: CONTACT_TYPES.COUPLE,
                        ...splitName(signer.name),
                        ...fiscal,
                    });
                    await addEventContact(event.id, nuevo.id, { role_in_event: "Firmante del contrato" });

                    signer.contactId = nuevo.id;
                    eventContacts.push({ ...nuevo, is_primary: false });
                    creados++;
                }

                // Los huecos pasan a apuntar a su ficha: a partir de aqui el
                // desplegable los ofrece y el proximo guardado ya actualiza.
                for (const n of [1, 2]) {
                    const sel = $(`.sel-s${n}-contact`);
                    if (sel) sel.innerHTML = signerOptions(n);
                }

                saveBtn.innerHTML = '<i class="fa-solid fa-check text-[10px]"></i> Guardado';
                saveBtn.classList.add("bg-emerald-100", "text-emerald-700");
                saveBtn.classList.remove("bg-slate-100", "text-slate-700");
                actions.addToast?.({
                    type: "success",
                    message: creados
                        ? `Datos guardados · ${creados} contacto${creados > 1 ? "s" : ""} nuevo${creados > 1 ? "s" : ""} vinculado${creados > 1 ? "s" : ""} al evento`
                        : targets.length > 1
                            ? "Datos guardados en las fichas de los dos firmantes"
                            : "Datos guardados en la ficha del firmante",
                });

                setTimeout(() => {
                    saveBtn.innerHTML = original;
                    saveBtn.disabled = false;
                    saveBtn.classList.remove("bg-emerald-100", "text-emerald-700");
                    saveBtn.classList.add("bg-slate-100", "text-slate-700");
                }, 2000);
            } catch (err) {
                console.error("[Preflight] error guardando firmante:", err);
                // Crear contactos exige rol admin o manager: sin el mensaje real
                // el fallo por permisos parece un error generico del CRM.
                actions.addToast?.({
                    type: "error",
                    message: `Error al guardar el firmante: ${err?.message || err}`,
                });
                saveBtn.innerHTML = original;
                saveBtn.disabled = false;
            }
        };
    }

    // Load Preview
    if (teamId) {
        previewNextDocNumber(teamId, docTypeCode).then(num => {
            const el = modalEl.querySelector(".txt-docnum");
            if (el) el.textContent = num;
        }).catch(() => {
            const el = modalEl.querySelector(".txt-docnum");
            if (el) el.textContent = `${docTypeCode}-${new Date().getFullYear()}-XXXX`;
        });
    }

    // Generate Final
    const genBtnFinal = modalEl.querySelector(".btn-generate-doc");
    genBtnFinal.onclick = async () => {
        // El numero correlativo se consume de forma irreversible, asi que los
        // datos del contrato se validan ANTES de pedirlo.
        if (isContrato) {
            const missing = contractErrors();
            if (missing.length > 0) {
                const banner = modalEl.querySelector(".error-banner");
                if (banner) {
                    banner.classList.remove("hidden");
                    banner.querySelector("p").innerHTML =
                        `<i class="fa-solid fa-triangle-exclamation text-rose-500"></i> Faltan datos: ${missing.join(", ")}.`;
                }
                actions.addToast?.({ type: "warning", message: "Completa los datos del contrato" });
                return;
            }
            modalEl.querySelector(".error-banner")?.classList.add("hidden");
        }

        try {
            genBtnFinal.disabled = true;
            genBtnFinal.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin text-xs"></i> Generando...';

            // El reparto pactado se guarda en el evento antes de emitir: si no,
            // el contrato diria un porcentaje y la senal esperada en el evento
            // seguiria calculandose con otro. No bloquea la descarga si falla.
            if (isContrato && event?.id && Number(event.deposit_pct) !== contractState.reservaPct) {
                try {
                    await updateEvent(event.id, { deposit_pct: contractState.reservaPct });
                    event.deposit_pct = contractState.reservaPct;
                } catch (e) {
                    console.warn("[Preflight] no se pudo guardar el reparto en el evento:", e);
                }
            }

            const docNumber = await incrementDocNumber(teamId, docTypeCode);
            close();
            await onGenerate(event, enrichedContact, company, docNumber, isContrato ? contractState : undefined);
        } catch (e) {
            console.error("[Preflight] error:", e);
            actions.addToast?.({ type: "error", message: "Error al generar documento" });
            genBtnFinal.disabled = false;
            genBtnFinal.innerHTML = `<i class="fa-solid fa-file-pdf text-xs"></i> Generar ${docLabel}`;
        }
    };
}
